import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    QuotaCreate as QuotaCreateMutation,
    QuotaCreateVariables,
    UserQuotaFragment,
} from 'nutshell-graphql-types';

import CREATE_QUOTA from '../mutations/create-quota.graphql';

import {addUsersToConnection, addQuotaToYearOverviewStatsCache} from './helpers';

type CreateQuotaInput = {
    userId: string;
    fiscalYear: number;
    monthValues: {month: number; value: number}[];
};

export function useCreateQuota(): {
    createQuota: (input: CreateQuotaInput, newUser: UserQuotaFragment) => Promise<any>;
    isLoading: boolean;
    errorMessage: string | null | undefined;
} {
    const [mutate, {loading, error}] = useMutation<QuotaCreateMutation, QuotaCreateVariables>(
        CREATE_QUOTA
    );

    return {
        createQuota: (input: CreateQuotaInput, newUser: UserQuotaFragment) => {
            const optimisticUser = buildOptimisticUser(newUser, input);

            return mutate({
                variables: {
                    input: {
                        userIds: [input.userId],
                        fiscalYear: input.fiscalYear,
                        monthValues: input.monthValues,
                    },
                },
                optimisticResponse: {
                    quotaCreate: {
                        __typename: 'QuotaCreatePayload' as const,
                        users: [optimisticUser],
                    },
                },
                update: (cache, {data}) => {
                    const users = data?.quotaCreate?.users;
                    if (!users?.length) return;
                    addUsersToConnection(cache, users);
                    addQuotaToYearOverviewStatsCache(cache, input.fiscalYear, input.monthValues);
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
    };
}

function buildOptimisticUser(user: UserQuotaFragment, input: CreateQuotaInput): UserQuotaFragment {
    return {
        ...user,
        quotas: [
            ...user.quotas,
            {
                __typename: 'Quota' as const,
                fiscalYear: input.fiscalYear,
                months: input.monthValues.map((mv) => ({
                    __typename: 'Month' as const,
                    identifier: mv.month,
                    value: {
                        __typename: 'Money' as const,
                        amount: mv.value,
                    },
                })),
            },
        ],
    };
}
